/**
 * QuickExpenseForm - Simplified expense entry
 * Creates journal entries automatically without accounting knowledge
 */

import React, { useState, useEffect } from 'react';
import { Account, TaxRate, QuickExpenseCreateRequest, QuickExpenseLine } from '../../../types/accounting';
import EntryAttachments from '../EntryAttachments';
import { accountingApi } from '../../../services/accountingApi';

interface QuickExpenseFormProps {
  businessId: number;
  accounts: Record<string, Account[]>;
  currency: 'CAD' | 'USD';
  onSuccess: () => void;
  onCancel: () => void;
}

interface ExpenseLineForm {
  amount: string;
  expense_account_id: number | null;
  tax_rate_id: number | null;
}

const QuickExpenseForm: React.FC<QuickExpenseFormProps> = ({
  businessId,
  accounts,
  currency,
  onSuccess,
  onCancel
}) => {
  // Form state
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [lines, setLines] = useState<ExpenseLineForm[]>([
    { amount: '', expense_account_id: null, tax_rate_id: null }
  ]);
  const [paymentAccountId, setPaymentAccountId] = useState<number | null>(null);
  // Receipts show the total including tax, so that is the default way to type one in
  const [amountsIncludeTax, setAmountsIncludeTax] = useState(true);
  const [vendor, setVendor] = useState('');
  const [description, setDescription] = useState('');

  // Data state
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Receipt state
  const [savedEntryId, setSavedEntryId] = useState<number | null>(null);

  // Load tax rates on mount
  useEffect(() => {
    loadTaxRates();
  }, [businessId]);

  const loadTaxRates = async () => {
    try {
      const response = await accountingApi.getTaxRates(businessId);
      if (response.status === 'success' && response.data?.tax_rates) {
        setTaxRates(response.data.tax_rates);
        // Set default tax rate for first line
        const defaultRate = response.data.tax_rates.find(r => r.is_default);
        if (defaultRate) {
          // Tax rates arrive after the form is on screen: fill in the default without wiping what was typed
          setLines(current => current.map((line, index) => (
            index === 0 && !line.amount && line.expense_account_id === null
              ? { ...line, tax_rate_id: defaultRate.id }
              : line
          )));
        }
      } else {
        // No tax rates - try creating defaults
        await createDefaultTaxRates();
      }
    } catch (err) {
      console.error('Failed to load tax rates:', err);
      // Try creating defaults
      await createDefaultTaxRates();
    }
  };

  const createDefaultTaxRates = async () => {
    try {
      const response = await accountingApi.createDefaultTaxRates(businessId);
      if (response.status === 'success' && response.data?.tax_rates) {
        setTaxRates(response.data.tax_rates);
        const defaultRate = response.data.tax_rates.find(r => r.is_default);
        if (defaultRate) {
          // Tax rates arrive after the form is on screen: fill in the default without wiping what was typed
          setLines(current => current.map((line, index) => (
            index === 0 && !line.amount && line.expense_account_id === null
              ? { ...line, tax_rate_id: defaultRate.id }
              : line
          )));
        }
      }
    } catch (err) {
      console.error('Failed to create default tax rates:', err);
    }
  };

  // Get expense accounts (type = 'expense')
  const expenseAccounts = accounts['expense'] || [];

  // What you can pay from: cash, bank or credit card, or leave it on account. Never a tax account.
  const isSalesTaxAccount = (name: string) =>
    ['hst', 'gst', 'pst', 'qst', 'sales tax'].some(token => name.toLowerCase().includes(token));
  const paymentAccounts = [
    ...(accounts['asset'] || []).filter(a =>
      ['cash', 'bank', 'chequing', 'checking', 'savings'].some(word => a.account_name.toLowerCase().includes(word))
    ),
    ...(accounts['liability'] || []).filter(a =>
      (a.account_name.toLowerCase().includes('credit card') || a.account_name.toLowerCase().includes('accounts payable'))
      && !isSalesTaxAccount(a.account_name)
    )
  ];

  /** The line's amount before tax. The backend always wants net, whichever way the user typed it. */
  const lineNet = (line: ExpenseLineForm) => {
    const typed = parseFloat(line.amount) || 0;
    const rate = taxRates.find(r => r.id === line.tax_rate_id)?.rate || 0;
    if (!amountsIncludeTax || rate === 0) return typed;
    return Math.round((typed / (1 + rate)) * 100) / 100;
  };

  // Calculate totals
  const calculateTotals = () => {
    let netTotal = 0;
    let taxTotal = 0;

    lines.forEach(line => {
      const net = lineNet(line);
      netTotal += net;
      const taxRate = taxRates.find(r => r.id === line.tax_rate_id);
      if (taxRate) {
        // the backend rounds each line's tax the same way
        taxTotal += Math.round(net * taxRate.rate * 100) / 100;
      }
    });

    return {
      net: Math.round(netTotal * 100) / 100,
      tax: Math.round(taxTotal * 100) / 100,
      gross: Math.round((netTotal + taxTotal) * 100) / 100
    };
  };

  const totals = calculateTotals();

  // Line management
  const addLine = () => {
    const defaultTaxRateId = taxRates.find(r => r.is_default)?.id || null;
    setLines([...lines, { amount: '', expense_account_id: null, tax_rate_id: defaultTaxRateId }]);
  };

  const removeLine = (index: number) => {
    if (lines.length > 1) {
      setLines(lines.filter((_, i) => i !== index));
    }
  };

  const updateLine = (index: number, field: keyof ExpenseLineForm, value: string | number | null) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  // Format currency for display
  const locale = currency === 'CAD' ? 'en-CA' : 'en-US';
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Submit handler
  const handleSubmit = async (addAnother: boolean = false) => {
    setError(null);

    // Validation
    if (!expenseDate) {
      setError('Date is required');
      return;
    }

    const validLines = lines.filter(l => l.amount && parseFloat(l.amount) > 0 && l.expense_account_id);
    if (validLines.length === 0) {
      setError('At least one expense line with amount and category is required');
      return;
    }

    if (!paymentAccountId) {
      setError('Payment account is required');
      return;
    }

    setLoading(true);

    try {
      const expenseLines: QuickExpenseLine[] = validLines.map(line => ({
        amount: lineNet(line),
        expense_account_id: line.expense_account_id!,
        tax_rate_id: line.tax_rate_id || undefined
      }));

      const request: QuickExpenseCreateRequest = {
        expense_date: expenseDate,
        lines: expenseLines,
        payment_account_id: paymentAccountId,
        vendor: vendor || undefined,
        description: description || undefined
      };

      const response = await accountingApi.createQuickExpense(businessId, request);

      if (response.status === 'success') {
        setSuccess(true);

        // Capture entry ID for receipt attachment
        const entryId = response.data?.journal_entry_id;
        if (entryId) {
          setSavedEntryId(entryId);
        }

        if (addAnother) {
          // Reset form for next entry
          const defaultTaxRateId = taxRates.find(r => r.is_default)?.id || null;
          setLines([{ amount: '', expense_account_id: null, tax_rate_id: defaultTaxRateId }]);
          setVendor('');
          setDescription('');
          setSuccess(false);
          setSavedEntryId(null);
          setTimeout(() => setError(null), 100);
        } else if (!entryId) {
          // Close form after short delay only if no receipt option
          setTimeout(() => {
            onSuccess();
          }, 1000);
        }
      } else {
        setError(response.message || 'Failed to save expense');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px' }}>
      <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Add Expense</h3>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {success && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #059669',
          borderRadius: '4px',
          color: '#059669'
        }}>
          Expense saved!
        </div>
      )}

      {/* Date */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Date</label>
        <input
          type="date"
          value={expenseDate}
          onChange={(e) => setExpenseDate(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      {/* Expense Lines */}
      <div style={{
        marginBottom: '16px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <label style={{ fontWeight: 500 }}>
            Expense Lines {lines.length > 1 && `(${lines.length})`}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={amountsIncludeTax}
              onChange={(e) => setAmountsIncludeTax(e.target.checked)}
            />
            Amounts include tax (as shown on the receipt)
          </label>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '100px 1fr 120px 30px',
          gap: '8px',
          marginBottom: '4px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.03em'
        }}>
          <div>{amountsIncludeTax ? 'Amount incl. tax' : 'Amount before tax'}</div>
          <div>Category</div>
          <div>Tax</div>
          <div />
        </div>

        {lines.map((line, index) => (
          <div key={index} style={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr 120px 30px',
            gap: '8px',
            marginBottom: '8px',
            alignItems: 'center'
          }}>
            {/* Amount */}
            <input
              type="number"
              placeholder="$0.00"
              value={line.amount}
              onChange={(e) => updateLine(index, 'amount', e.target.value)}
              style={{
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              step="0.01"
              min="0"
            />

            {/* Category */}
            <select
              value={line.expense_account_id || ''}
              onChange={(e) => updateLine(index, 'expense_account_id', e.target.value ? parseInt(e.target.value) : null)}
              style={{
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select category...</option>
              {expenseAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name}
                </option>
              ))}
            </select>

            {/* Tax Rate */}
            <select
              value={line.tax_rate_id || ''}
              onChange={(e) => updateLine(index, 'tax_rate_id', e.target.value ? parseInt(e.target.value) : null)}
              style={{
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">No Tax</option>
              {taxRates.map(rate => (
                <option key={rate.id} value={rate.id}>
                  {rate.name} {rate.rate_percent}%
                </option>
              ))}
            </select>

            {/* Remove button */}
            {lines.length > 1 && (
              <button
                type="button"
                onClick={() => removeLine(index)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
                title="Remove line"
              >
                x
              </button>
            )}
          </div>
        ))}

        {/* Add line button */}
        <button
          type="button"
          onClick={addLine}
          style={{
            marginTop: '8px',
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: '1px dashed #9ca3af',
            borderRadius: '4px',
            color: '#6b7280',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          + Add Another Line (Split Expense)
        </button>
      </div>

      {/* Totals */}
      <div style={{
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#f0fdf4',
        borderRadius: '4px',
        border: '1px solid #bbf7d0',
        textAlign: 'right'
      }}>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          Subtotal: {formatCurrency(totals.net)}
        </div>
        {totals.tax > 0 && (
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            Tax: {formatCurrency(totals.tax)}
          </div>
        )}
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#059669', marginTop: '4px' }}>
          Total: {formatCurrency(totals.gross)}
        </div>
      </div>

      {/* Payment Account */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Paid From</label>
        <select
          value={paymentAccountId || ''}
          onChange={(e) => setPaymentAccountId(e.target.value ? parseInt(e.target.value) : null)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: 'white'
          }}
        >
          <option value="">Select payment account...</option>
          {paymentAccounts.map(account => (
            <option key={account.id} value={account.id}>
              {account.account_name}
            </option>
          ))}
        </select>
      </div>

      {/* Vendor (optional) */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
          Vendor <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
        </label>
        <input
          type="text"
          placeholder="e.g., Staples, Tim Hortons"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      {/* Notes (optional) */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
          Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
        </label>
        <input
          type="text"
          placeholder="Brief description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
      </div>

      {/* Invoices and receipts, once the entry exists to hang them on */}
      {savedEntryId && success && (
        <div style={{ marginBottom: '16px', padding: '16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <EntryAttachments businessId={businessId} entryId={savedEntryId} />
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        {savedEntryId && success ? (
          <button
            type="button"
            onClick={onSuccess}
            style={{
              padding: '10px 20px',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Done
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : 'Save & Add Another'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default QuickExpenseForm;
